import { Button } from "@/components/ui/button";
import AuthContext from "@/store/contexts/AuthContext";
import { ComponentConfig } from "@measured/puck";
import React, { useContext, useEffect, useRef, useState } from "react";
import { BookTemplate, LucideProps } from "lucide-react";
import dynamicIconImports from "lucide-react/dynamicIconImports";
import { sizeOptions } from "@/puck/options";
import DataBlockItemLoader from "@/components/common/DataBlockItem/DataBlockItemLoader";
import BlockTemplateDialog from "@/components/pages/home/BlockTemplateDialog";

type DynamicIconProps = {
  name: string;
  size?: number;
  color?: string;
};

export const DynamicLucideIcon: React.FC<DynamicIconProps> = ({
  name,
  size = 18,
  color = "currentColor",
}) => {
  const [Icon, setIcon] = useState<any>(null);

  const loadIcon = async () => {
    const icon = await dynamicIconImports[
      name as keyof typeof dynamicIconImports
    ]();
    setIcon(icon);
  };

  useEffect(() => {
    if (name === "none" || !name) return;
    loadIcon();
  }, [name]);

  if (name === "none" || !name) return null;

  // Create props object that matches LucideProps interface
  const iconProps: LucideProps = {
    size,
    color,
  };

  if (!Icon) {
    return null;
  }

  // Create the component from the imported module
  const IconComponent = Icon.default;
  return <IconComponent {...iconProps} />;
};

export type DataProps = {
  gridColumnSpan: number;
  gridRowSpan: number;
  configuration: string;
};

const DataConfiguration = ({ id }: { id: string }) => {
  const { openGraphConfiguration } = useContext(AuthContext);
  return (
    <div className="flex flex-col items-center gap-2">
      <Button
        className="w-full"
        onClick={() =>
          openGraphConfiguration({ id: id.split("_")[0], type: "block" })
        }
      >
        Update block configuration
      </Button>
      <span>or</span>
      <BlockTemplateDialog id={id.split("_")[0]} blockType="data">
        <Button className="w-full" size="lg" variant={"outline"}>
          <BookTemplate />
          Use a data block template
        </Button>
      </BlockTemplateDialog>
    </div>
  );
};

export const DataBlock: ComponentConfig<DataProps> = {
  label: "Data block",
  fields: {
    gridColumnSpan: {
      type: "select",
      label: "Width",
      options: sizeOptions,
    },
    gridRowSpan: {
      type: "select",
      label: "Height",
      options: sizeOptions,
    },
    configuration: {
      type: "custom",
      render: ({ id }) => {
        return <DataConfiguration id={id} />;
      },
    },
  },
  defaultProps: {
    gridColumnSpan: 1,
    gridRowSpan: 1,
    configuration: "",
  },
  render: ({ id, gridColumnSpan, gridRowSpan, puck }) => {
    const itemRef = useRef<HTMLDivElement>(null);

    const col = [
      "col-span-1",
      "col-span-2",
      "col-span-2 xl:col-span-3",
      "col-span-2 xl:col-span-4",
    ];
    const row = ["row-span-1", "row-span-2", "row-span-3", "row-span-4"];

    const style = {
      minHeight: `${120 * gridRowSpan}px`,
      height: "100%",
    };

    if (puck.isEditing && itemRef.current?.parentElement) {
      for (let index = 0; index < col.length; index++) {
        const colSpan = col[index];
        itemRef.current.parentElement.classList.remove(...colSpan.split(" "));
      }

      for (let index = 0; index < row.length; index++) {
        const rowSpan = row[index];
        itemRef.current.parentElement.classList.remove(rowSpan);
      }

      itemRef.current.parentElement.classList.add(
        ...col[gridColumnSpan - 1].split(" ")
      );
      itemRef.current.parentElement.classList.add(row[gridRowSpan - 1]);
    }

    return (
      <div
        ref={itemRef}
        id={id}
        style={style}
        className={`${col[gridColumnSpan - 1]} ${
          row[gridRowSpan - 1]
        } page-break`}
      >
        <DataBlockItemLoader id={id} editing={puck.isEditing} />
      </div>
    );
  },
};
